export default class Accordion {
  private static instances: Accordion[] = [];

  private el: HTMLDetailsElement;

  private summary: HTMLElement;

  private content: HTMLElement;

  private animation: Animation | null = null;

  private isClosing = false;

  private isExpanding = false;

  private boundOnClick: (e: MouseEvent) => void;

  private groupRoot: Element;

  constructor(el: HTMLDetailsElement, groupRoot?: Element) {
    this.el = el;

    const summary = el.querySelector('summary');
    const content = el.querySelector('summary + :last-child');

    if (!summary || !(summary instanceof HTMLElement)) {
      throw new Error('Accordion requires a <summary> element.');
    }
    if (!content || !(content instanceof HTMLElement)) {
      throw new Error(
        'Accordion requires a wrapper element directly after <summary>.',
      );
    }

    this.summary = summary;
    this.content = content;
    this.boundOnClick = this.onClick.bind(this);

    this.groupRoot =
      groupRoot ||
      el.closest('[data-accordion-group], section') ||
      document.body;

    this.summary.addEventListener('click', this.boundOnClick);
    Accordion.instances.push(this);
  }

  private onClick(e: MouseEvent): void {
    e.preventDefault();
    this.el.classList.add('overflow-hidden');

    if (this.isClosing || !this.el.open) {
      this.open();
    } else if (this.isExpanding || this.el.open) {
      this.shrink();
    }
  }

  private shrink(awaitable = false): Promise<void> | void {
    this.isClosing = true;
    this.el.classList.add('overflow-hidden', 'is-closing');

    const startHeight = `${this.el.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight}px`;

    if (this.animation) this.animation.cancel();

    this.animation = this.el.animate(
      { height: [startHeight, endHeight] },
      this.getAnimationOptions(),
    );

    const { animation } = this;

    const done = new Promise<void>((resolve) => {
      animation.onfinish = () => {
        this.onAnimationFinish(false);
        resolve();
      };
      animation.oncancel = () => {
        this.isClosing = false;
        resolve();
      };
    });

    return awaitable ? done : undefined;
  }

  private open(): void {
    Accordion.instances.forEach((acc) => {
      if (acc !== this && acc.el.open && acc.groupRoot === this.groupRoot) {
        acc.shrink();
      }
    });

    this.el.style.height = `${this.el.offsetHeight}px`;
    this.el.open = true;

    window.requestAnimationFrame(() => this.expand());
  }

  private expand(): void {
    this.isExpanding = true;
    this.el.classList.add('overflow-hidden');

    const startHeight = `${this.el.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

    if (this.animation) this.animation.cancel();

    this.animation = this.el.animate(
      { height: [startHeight, endHeight] },
      this.getAnimationOptions(),
    );

    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => {
      this.isExpanding = false;
    };
  }

  private onAnimationFinish(open: boolean): void {
    this.el.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.el.style.height = '';
    this.el.classList.remove('overflow-hidden', 'is-closing');
  }

  public destroy(): void {
    this.summary.removeEventListener('click', this.boundOnClick);
    if (this.animation) this.animation.cancel();
    Accordion.instances = Accordion.instances.filter((acc) => acc !== this);
  }

  private getAnimationOptions(): KeyframeAnimationOptions {
    const durationAttr = this.el.getAttribute('data-accordion-duration');
    const easingAttr = this.el.getAttribute('data-accordion-easing');

    const duration = durationAttr ? parseInt(durationAttr, 10) : 300;
    const easing = easingAttr || 'ease';

    return { duration, easing };
  }
}
